import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useTransition } from '@react-spring/web';
import { useState } from 'react';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  GameplayTier,
} from '@sorare/core/src/__generated__/globalTypes';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';

import {
  NBACardFooter,
  useNBACardFooterEnabled,
} from '#src/components/card/NBACardFooter/index.tsx';
import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { PlayerDrawer } from '#src/components/player/PlayerDrawer/index.tsx';

import type { CardInfos_anyCardInterface } from './__generated__/index.graphql.ts';

const StyledAnimatedDiv = styled(animated.div)`
  position: absolute;
  bottom: calc(-1 * var(--margin-bottom-center-area));
`;

type Props = {
  card: CardInfos_anyCardInterface;
  showPlayerDetails?: boolean;
};
export const CardInfos = ({ card, showPlayerDetails }: Props) => {
  const [playerDetailsOpen, setPlayerDetailsOpen] = useState(false);
  const showNBACardFooter = useNBACardFooterEnabled(card.anyPlayer.sport);
  const transition = useTransition(card, {
    from: { opacity: 0, transform: 'translate3d(0, -32px ,0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0 ,0)', delay: 300 },
    leave: {
      opacity: 0,
      transform: 'translate3d(0, 0 ,0)',
      config: { duration: 100 },
    },
    exitBeforeEnter: true,
  });

  return (
    <>
      {transition((styles, currentCard) => {
        const gameplayTier =
          currentCard.anyPlayer.gameplayTier ?? GameplayTier.DNP;
        const openPlayerDetails = showPlayerDetails
          ? () => {
              setPlayerDetailsOpen(true);
            }
          : undefined;
        const l10Score = (shape: 'TEXT' | 'HEXAGON') => (
          <PlayerScore
            player={currentCard.anyPlayer}
            mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
            shape={shape}
            animated={false}
            hideTooltip
          />
        );
        return (
          <StyledAnimatedDiv style={styles}>
            {showNBACardFooter ? (
              <NBACardFooter
                anyPlayer={currentCard.anyPlayer}
                score={l10Score('TEXT')}
                onClick={openPlayerDetails}
              />
            ) : (
              <CardFooter
                left={
                  <AnyPlayerStatsIcon
                    backgroundColor="transparent"
                    player={currentCard.anyPlayer}
                  />
                }
                right={<GameplayTierStars gameplayTier={gameplayTier} />}
                onClick={openPlayerDetails}
              >
                {l10Score('HEXAGON')}
              </CardFooter>
            )}
          </StyledAnimatedDiv>
        );
      })}
      <PlayerDrawer
        cardOpened={
          showPlayerDetails && playerDetailsOpen
            ? {
                playerSlug: card.anyPlayer.slug,
                cardSlug: card.slug,
              }
            : null
        }
        onClose={() => {
          setPlayerDetailsOpen(false);
        }}
      />
    </>
  );
};

CardInfos.fragments = {
  anyCardInterface: gql`
    fragment CardInfos_anyCardInterface on AnyCardInterface {
      slug
      anyPlayer {
        slug
        gameplayTier
        ...AnyPlayerStatsIcon_anyPlayer
        ...NBACardFooter_anyPlayerInterface
        ...PlayerScore_anyPlayer
      }
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${NBACardFooter.fragments.anyPlayerInterface}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<CardInfos_anyCardInterface>,
};
